import { VStack } from "@/components/Layout";
import HStack from "@/components/Layout/HStack";
import NavigationCard, {
  type NavigationSectionGroup,
} from "@/components/Navigation/NavigationCard";
import { type PropsWithChildren, useEffect } from "react";
import { CFormStore, useCFormStore } from "./cform-store";
import type { CFormEditUnit, CFormItemLayoutType } from "./cform-types";

type Props = PropsWithChildren<{
  /** 編集する単位 */
  editUnit: CFormEditUnit;
  /** 各Itemの見た目（デフォルトでカード表示） */
  itemLayout?: CFormItemLayoutType;
  /** 指定するとナビゲーションを描画 */
  navigationSectionGroups?: NavigationSectionGroup[];
  onSave?: () => Promise<unknown>;
  onReset?: () => void;
}>;

/**
 * Card-based Form / Page
 *
 * DRESS CODE 標準の、台帳系詳細画面のプロフィール(編集モードつき) を簡潔に記述するためのレイアウトです。
 *
 * Navigation と Sections が一体になり、そのままページコンテンツとして活用できます。
 * 以下の形でレイアウトを構成することにより、適切に Navigation を描画します。
 *
 * また、 CForm は cform-store によって状態管理をしており、
 * CForm 内部の CFormSection / CFormItem に対して編集状態等のコントロールを自動的に行います。
 *
 * <CForm>
 *   <CFormSection>
 *     <CFormItem />
 *      ...
 *   <CFormSection />
 *   ...
 * </CForm>
 */
export function CForm(props: Props) {
  const store = CFormStore.createStore();
  return (
    <CFormStore.Provider value={store}>
      <Content {...props} />
    </CFormStore.Provider>
  );
}

function Content(props: Props) {
  const store = useCFormStore();
  useEffect(() => store.actions.init(props), [store.actions.init, props]);

  return (
    <HStack className="p-8 gap-8">
      {/* Navigation */}
      {props.navigationSectionGroups && (
        <nav className="sticky top-8 h-fit w-60">
          <NavigationCard sections={props.navigationSectionGroups} />
        </nav>
      )}

      {/* Contents */}
      <VStack className="w-full gap-4">{props.children}</VStack>
    </HStack>
  );
}
